import { checkedTrackedItems } from "../../lib/tracked-items";
import { receiptSplitExpense } from "../../lib/receipt-splits";
import { normalizeLedgerFields } from "../../lib/ledger-field-compat";
import { useEffect, useRef, useState } from "react";
import type { Transaction } from "../../types";
import type { ReceiptDraft } from "../../types/receipt";
import type { ReceiptState } from "../../lib/receipt-draft";
import { api, ApiError } from "../../lib/api";
import { receiptImageGroups, type ReceiptAttachment } from "../../lib/image-data";

/** A receipt attachment has its own durable journal and never enters the expense-create queue. */
export function useTransactionReceipt(transaction: Transaction, sourceReceiptId: string | undefined, onSaved: () => void) {
  const key = `barnyard-receipt-attachment-${transaction.id}-${sourceReceiptId || "new"}`;
  const [state, setState] = useState<ReceiptState | null>(() => {
    try { return normalizeLedgerFields(JSON.parse(localStorage.getItem(key) || "null")) as ReceiptState | null; }
    catch { return null; }
  });
  const [attachments, setAttachments] = useState<ReceiptAttachment[]>([]);
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const lock = useRef(false), mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  function update(next: ReceiptState | null) {
    if (next) localStorage.setItem(key, JSON.stringify(next));
    else localStorage.removeItem(key);
    if (mounted.current) setState(next);
  }
  function editState(patch: Partial<ReceiptState>) {
    if (!state) return;
    try { update({ ...state, ...patch, updated: Date.now() }); }
    catch { setError("Enable browser storage to keep this receipt review before saving."); }
  }
  const editDraft = (patch: Partial<ReceiptDraft>) => state && editState({ draft: { ...state.draft, ...patch } });
  async function scan(extract = true) {
    if (lock.current || (!attachments.length && !sourceReceiptId)) return;
    lock.current = true; setBusy(true); setError("");
    try {
      const input = sourceReceiptId ? { receiptId: sourceReceiptId } : { ...await receiptImageGroups(attachments), extract };
      const { receipts } = await api<{ receipts: ReceiptDraft[] }>("receipts/scan", input);
      if (!receipts?.length) throw new Error("No receipts were returned. Try the photos again.");
      update({ draft: receipts[0], remainingDrafts: receipts.slice(1), step: "details", pending: false,
        updated: Date.now(), receiptNumber: 1, receiptTotal: receipts.length, trackingItemIds: [] });
      if (mounted.current) setAttachments([]);
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : "Could not prepare the receipt.");
    } finally {
      lock.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  async function save() {
    if (!state || lock.current) return;
    lock.current = true; setBusy(true); setError("");
    let attached = false;
    try {
      const draft = { ...state.draft, expense: receiptSplitExpense(state.draft) };
      const selected = draft.items.filter(item => state.trackingItemIds?.includes(item.id));
      if (selected.length && !draft.currency) throw new Error("Choose the receipt currency before tracking prices.");
      checkedTrackedItems(selected.map(item => ({ ...item, notes: "" })));
      update({ ...state, pending: true });
      const result = await api<{ verified: boolean }>("receipts/attach", { transactionId: transaction.id, draft });
      if (!result.verified) throw new Error("Attachment verification is pending. Retry to check the save.");
      attached = true;
      for (const image of draft.pendingImages || [])
        await api("receipts/image", { receiptId: draft.id, ...image });
      for (const item of selected) {
        const price = await api<{ verified: boolean }>("prices", { receiptId: draft.id, item: { ...item, group: item.group || item.name } });
        if (!price.verified) throw new Error("Price verification is pending.");
      }
      const [next, ...remainingDrafts] = state.remainingDrafts || [];
      update(next ? { ...state, draft: next, remainingDrafts, pending: false, trackingItemIds: [],
        receiptNumber: (state.receiptNumber || 1) + 1, updated: Date.now() } : null);
      if (!next && mounted.current) onSaved();
    } catch (e) {
      if (!mounted.current) return;
      if (!attached && e instanceof ApiError && e.status >= 400 && e.status < 500)
        editState({ pending: false });
      setError(attached
        ? "Receipt attached. Some photos or selected prices still need saving. Retry to finish safely."
        : e instanceof Error ? e.message : "Could not attach the receipt. Retry to check the save.");
    } finally {
      lock.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return { state, attachments, setAttachments, busy, error, setError, editState, editDraft, scan, save,
    discard: () => { try { update(null); setError(""); } catch { setError("Could not discard the draft."); } } };
}
